import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Activity, ArrowUpRight, CheckCircle2, XCircle, Upload, MessageSquare, DollarSign, Database, Clock, Truck } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale/es';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_META = {
  crear_pago: { icon: DollarSign, tone: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-950/40' },
  aprobar_pago: { icon: CheckCircle2, tone: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-950/40' },
  rechazar_pago: { icon: XCircle, tone: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-950/40' },
  subir_archivo: { icon: Upload, tone: 'text-slate-600 dark:text-slate-300', bg: 'bg-slate-100 dark:bg-slate-800/60' },
  enviar_mensaje: { icon: MessageSquare, tone: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-950/40' },
  marcar_macrobase: { icon: Database, tone: 'text-teal-600 dark:text-teal-400', bg: 'bg-teal-50 dark:bg-teal-950/40' },
  bitacora_vehiculo: { icon: Truck, tone: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/40' },
};

export default function RecentActivity({ limit = 8, compact = false }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/activity/recent/feed?limit=${limit}`);
        if (mounted) setItems(res.data || []);
      } catch (e) {
        // silencioso
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    const int = setInterval(load, 45000);
    return () => { mounted = false; clearInterval(int); };
  }, [limit]);

  return (
    <Card className="p-5 border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900" data-testid="recent-activity-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200/70 dark:from-blue-950/50 dark:to-blue-900/40 flex items-center justify-center">
            <Activity className="h-4 w-4 text-[#2c57c5] dark:text-blue-300" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Actividad reciente</h3>
            <p className="text-[11px] text-slate-500 dark:text-slate-500">Últimos movimientos</p>
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-600 font-semibold">En vivo</span>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-start gap-3 animate-pulse">
              <div className="h-8 w-8 rounded-lg bg-slate-100 dark:bg-slate-800" />
              <div className="flex-1 space-y-2">
                <div className="h-3 rounded bg-slate-100 dark:bg-slate-800 w-3/4" />
                <div className="h-2 rounded bg-slate-100 dark:bg-slate-800 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="py-6 text-center">
          <Clock className="h-8 w-8 text-slate-300 dark:text-slate-700 mx-auto mb-2" />
          <p className="text-xs text-slate-500 dark:text-slate-500">Aún no hay actividad</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((it) => {
            const meta = ACTION_META[it.action] || { icon: Activity, tone: 'text-slate-500 dark:text-slate-400', bg: 'bg-slate-100 dark:bg-slate-800/60' };
            const Icon = meta.icon;
            let when = '';
            try {
              when = formatDistanceToNow(new Date(it.created_at), { addSuffix: true, locale: es });
            } catch { /* ignore */ }
            return (
              <li key={it.id}>
                <button
                  onClick={() => navigate(`/payments/${it.payment_id}`)}
                  className="w-full flex items-start gap-3 text-left rounded-lg p-2 -mx-2 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors group"
                  data-testid={`activity-item-${it.id}`}
                >
                  <div className={`h-8 w-8 rounded-lg ${meta.bg} flex items-center justify-center shrink-0`}>
                    <Icon className={`h-4 w-4 ${meta.tone}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] text-slate-800 dark:text-slate-200 leading-snug truncate">
                      <span className="font-semibold">{it.user_name}</span>{' '}
                      <span className="text-slate-500 dark:text-slate-400">
                        {actionLabel(it.action)}
                      </span>{' '}
                      {it.proveedor && (
                        <span className="text-slate-700 dark:text-slate-300 font-medium">{it.proveedor}</span>
                      )}
                    </p>
                    {!compact && it.details && (
                      <p className="text-[11px] text-slate-500 dark:text-slate-500 line-clamp-1 mt-0.5">{it.details}</p>
                    )}
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-slate-400 dark:text-slate-600">{when}</span>
                      {typeof it.monto === 'number' && (
                        <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400">
                          Q{it.monto.toLocaleString('es-GT', { minimumFractionDigits: 2 })}
                        </span>
                      )}
                    </div>
                  </div>
                  <ArrowUpRight className="h-3.5 w-3.5 text-slate-300 dark:text-slate-700 group-hover:text-[#2c57c5] dark:group-hover:text-blue-400 transition-colors shrink-0 mt-1" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function actionLabel(action) {
  switch (action) {
    case 'crear_pago': return 'creó un pago para';
    case 'aprobar_pago': return 'aprobó el pago de';
    case 'rechazar_pago': return 'rechazó el pago de';
    case 'subir_archivo': return 'subió un archivo a';
    case 'enviar_mensaje': return 'comentó en';
    case 'marcar_macrobase': return 'ingresó a ERP el pago de';
    case 'bitacora_vehiculo': return 'actualizó la bitácora del vehículo de';
    default: return 'actualizó';
  }
}
